/**
 * useCauseRecords - Data fetching for root cause specific records
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { FilterConfig } from '../../../records/filter';
import type {
  CauseReportCauseRecords,
  UseCauseRecordsReturn,
} from '../types';
import {
  topCausesKeys,
  CAUSE_REPORT_STALE_TIME,
  DIALOG_RECORDS_PAGE_SIZE,
} from '../constants';
import { fetchWithAuth } from '../../../../../utils/api';
import { mapFiltersToApiParams, transformRecord } from '../utils';
import type { ApiRecord } from '../utils';

interface CauseRecordsApiResponse {
  records: ApiRecord[];
  total_records: number;
  cause_id: string;
  cause_name: string;
  page: number;
  page_size: number;
  total_pages: number;
}

function transformCauseRecords(data: CauseRecordsApiResponse): CauseReportCauseRecords {
  return {
    records: data.records.map(transformRecord),
    totalRecords: data.total_records,
    causeId: data.cause_id,
    causeName: data.cause_name,
    page: data.page,
    pageSize: data.page_size,
    totalPages: data.total_pages,
  };
}

async function fetchCauseRecords(
  datasetId: string,
  causeId: string,
  params: Record<string, string>,
  page: number
): Promise<CauseRecordsApiResponse> {
  const queryParams = { ...params };
  queryParams.page = String(page);
  queryParams.page_size = String(DIALOG_RECORDS_PAGE_SIZE);

  const queryString = new URLSearchParams(queryParams).toString();

  return fetchWithAuth(
    `/v1/datasets/${datasetId}/cause-report/causes/${causeId}/records?${queryString}`
  );
}

export function useCauseRecords(
  datasetId: string,
  causeId: string | null,
  filters: FilterConfig,
  page: number = 1,
  enabled: boolean = true
): UseCauseRecordsReturn {
  const filterParams = useMemo(() => mapFiltersToApiParams(filters), [filters]);

  const query = useQuery({
    queryKey: [...topCausesKeys.all, 'records', datasetId, causeId, filterParams, page],
    queryFn: () => fetchCauseRecords(datasetId, causeId!, filterParams, page),
    select: transformCauseRecords,
    staleTime: CAUSE_REPORT_STALE_TIME,
    enabled: !!datasetId && !!causeId && enabled,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
